import { beginLoading, endLoading } from '@app/AppLayout/LoadingContext';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DetailPageHeader } from '@app/components/DetailPageHeader';
import { NotFoundState } from '@app/components/NotFoundState';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { PhoneInput, isSamePhoneNumber } from '@app/components/PhoneInput';
import { isNotFound } from '@app/utils/ApiError';
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';
import { useEntity } from '@app/hooks/useEntity';
import { Country } from '@app/model/Country';
import { TrainingSpecialistStrategic } from '@app/model/TrainingSpecialistStrategic';
import { useCountryStore, useRedHatRegionStore, useTrainingSpecialistStrategicStore } from '@app/store/entityStore';
import {
  ActionList, ActionListItem, Alert, AlertVariant, Button, Card, Form, FormAlert, FormGroup,
  PageSection, Stack, TextInput, Title
} from '@patternfly/react-core';
import { TypeaheadSelect } from '@patternfly/react-templates';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type ValidateStatus = 'success' | 'warning' | 'error' | 'default';

const pristineValidation: Record<string, ValidateStatus> = { name: 'default', email: 'default' };

const TSSDetail: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const [tss, setTSS] = React.useState<Partial<TrainingSpecialistStrategic>>({});
  const [changeLogOpen, setChangeLogOpen] = React.useState<boolean>(false);
  const [validated, setValidated] = React.useState<Record<string, ValidateStatus>>(pristineValidation);
  const [isDirty, setIsDirty] = React.useState(false);
  /** No TSS answers to that id, so the page says so in place of the record. */
  const [notFound, setNotFound] = React.useState(false);

  const loadTSS = useTrainingSpecialistStrategicStore(state => state.loadById);
  const saveTSS = useTrainingSpecialistStrategicStore(state => state.save);
  const removeTSS = useTrainingSpecialistStrategicStore(state => state.delete);

  const { items: countries } = useEntity(useCountryStore);
  const { items: redHatRegions } = useEntity(useRedHatRegionStore);

  useBreadcrumbLabel(id === 'new' ? 'New' : tss.name);
  // The overview is unmounted, so this page has to name the crumb above it as well
  useBreadcrumbLabel('TSS', '/tss');

  // Options come straight from the loaded dictionaries, with `selected` mirroring the
  // record being edited. TypeaheadSelect only reads initialOptions when it mounts, so
  // the option count is part of its key below: without that, the lists stay empty for
  // whoever opens the page before the dictionaries finish loading.
  const countriesSelection = React.useMemo(
    () => countries.map((c) => ({
      content: `${c.flag} ${c.name}`,
      value: c.alpha2,
      selected: c.alpha2 === tss.country?.alpha2
    })),
    [countries, tss.country?.alpha2]
  );

  const redHatRegionsSelection = React.useMemo(
    () => redHatRegions.map((r) => ({
      content: r.name,
      value: r.id,
      selected: r.id === tss.redHatRegion?.id
    })),
    [redHatRegions, tss.redHatRegion?.id]
  );

  // Bumped whenever the record on screen is replaced wholesale, remounting both
  // typeaheads so they drop any text the user had typed and re-read their selection
  const [typeaheadKey, setTypeaheadKey] = React.useState<number>(0);

  const showRecord = (record: Partial<TrainingSpecialistStrategic>) => {
    setTSS(record);
    setValidated(pristineValidation);
    setIsDirty(false);
    setTypeaheadKey((key) => key + 1);
  };

  React.useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      if (id === 'new') return;

      setNotFound(false);
      beginLoading();
      try {
        const record = await loadTSS(id as string);
        // Cloned so editing the form does not mutate the record held in the store
        if (isMounted) showRecord(structuredClone(record));
      } catch (error: any) {
        // An id matching no TSS is the answer rather than a failure: the page renders it.
        if (!isMounted) return;
        if (isNotFound(error)) setNotFound(true);
        else addNotification('Error loading TSS', AlertVariant.danger, error.message);
      } finally {
        endLoading();
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [id]);

  const updateField = (field: keyof TrainingSpecialistStrategic, value: any) => {
    setTSS(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  /** Reformatting by the phone input itself is not an edit, and must not dirty the form */
  const handleMobileChange = (mobileNumber: string) => {
    if (isSamePhoneNumber(mobileNumber, tss.mobileNumber)) {
      setTSS(prev => ({ ...prev, mobileNumber }));
      return;
    }
    updateField('mobileNumber', mobileNumber);
  };

  /** Clears a field's error as soon as the user starts fixing it */
  const updateValidatedField = (field: 'name' | 'email', value: string) => {
    updateField(field, value);
    if (validated[field] === 'error') setValidated(prev => ({ ...prev, [field]: 'default' }));
  };

  const validate = () => {
    const newValidated: Record<string, ValidateStatus> = {
      name: tss.name?.trim() ? 'success' : 'error',
      email: tss.email?.trim() ? 'success' : 'error'
    };
    setValidated(newValidated);
    return !Object.values(newValidated).includes('error');
  };

  const onSaveClick = async () => {
    if (!validate()) return;

    beginLoading();
    try {
      const isNew = id === 'new';
      const savedData = await saveTSS(tss, isNew);

      showRecord(savedData);
      addNotification('TSS saved successfully', AlertVariant.success);

      if (isNew) navigate(`/tss/${savedData.id}`, { replace: true });
    } catch (error: any) {
      addNotification('Error saving TSS', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  const onCancelClick = async () => {
    if (id === 'new') {
      showRecord({});
    } else {
      const record = await loadTSS(id as string);
      showRecord(structuredClone(record));
    }
  };

  const onRefresh = async () => {
    if (id === 'new') return;
    const fresh = await loadTSS(id as string, true);
    showRecord(structuredClone(fresh));
  };

  const onRemove = async () => {
    beginLoading();
    try {
      await removeTSS(tss.id as number);
      addNotification('TSS removed successfully', AlertVariant.success);
      setIsDirty(false);
      // Same state the layout's Back button carries, so the list comes back filtered as it was
      navigate('/tss', { state: { restoreFilters: true } });
    } catch (error: any) {
      addNotification('Error removing TSS', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  if (notFound) {
    return <NotFoundState entityLabel="TSS" identifier={id} backTo={{ path: '/tss', label: 'Back to TSS list' }} />;
  }

  const changeLogTitle = tss.name;
  /**
   * Offered only once the record is stored: a form that has never been saved has no revision to
   * read, and the endpoint would answer 404 for the id it does not yet have.
   */
  const changeLogItems = tss.id
    ? [{ key: 'change-log', label: 'Change log', onClick: () => setChangeLogOpen(true) }]
    : [];


  return (
    <EntityChangeLog
      entity="tss"
      id={tss.id}
      entityLabel="this TSS"
      title={changeLogTitle}
      isOpen={changeLogOpen}
      onClose={() => setChangeLogOpen(false)}
    >
      <DetailPageHeader
        title={id === 'new' ? 'New TSS' : tss.name || 'Editing TSS'}
        actions={
          <ActionList>
            <ActionListItem>
              <Button variant="primary" id="save-button" isDisabled={!isDirty} onClick={onSaveClick}>Save</Button>
            </ActionListItem>
            <ActionListItem>
              <Button variant="link" id="cancel-button" isDisabled={!isDirty} onClick={onCancelClick}>Cancel</Button>
            </ActionListItem>
            <ActionListItem>
              <PageActionsMenu
                items={changeLogItems}
                onRefresh={onRefresh}
                onRemove={onRemove}
                isRemoveDisabled={id === 'new'}
                removeConfirmationMessage={`Confirm removal of "${tss.name}"?`}
              />
            </ActionListItem>
          </ActionList>
        }
      />

      <PageSection isWidthLimited>
        <Stack hasGutter>
          <Card style={{ padding: '24px' }}>
            <Title headingLevel="h2" size="xl" style={{ marginBottom: '24px' }}>General Information</Title>
            <Form isHorizontal>
              {Object.values(validated).includes('error') && (
                <FormAlert>
                  <Alert variant="danger" title="Fill out all required fields before saving." aria-live="polite" isInline />
                </FormAlert>
              )}

              <FormGroup label="Name" isRequired fieldId="name">
                <TextInput isRequired type="text" id="name" name="name"
                  value={tss.name || ''}
                  onChange={(_ev, v) => updateValidatedField('name', v)}
                  validated={validated.name}
                />
              </FormGroup>

              <FormGroup label="Region" fieldId="region">
                <TextInput type="text" id="region" name="region"
                  value={tss.region || ''} onChange={(_ev, v) => updateField('region', v)}
                />
              </FormGroup>

              <FormGroup label="Red Hat Region" fieldId="redHatRegion">
                <TypeaheadSelect
                  initialOptions={redHatRegionsSelection} key={`rhr-${typeaheadKey}-${redHatRegionsSelection.length}`}
                  selected={tss.redHatRegion?.id}
                  placeholder="Select a Red Hat Region" id='redHatRegion'
                  noOptionsFoundMessage={(filter) => `No Red Hat Region was found for "${filter}"`}
                  onClearSelection={() => updateField('redHatRegion', undefined)}
                  onSelect={(_ev, v) => updateField('redHatRegion', redHatRegions.find(r => r.id === v))}
                />
              </FormGroup>

              <FormGroup label="Country" fieldId="country">
                <TypeaheadSelect
                  initialOptions={countriesSelection} key={`ctry-${typeaheadKey}-${countriesSelection.length}`}
                  selected={tss.country?.alpha2}
                  placeholder="Select a country" id='country'
                  noOptionsFoundMessage={(filter) => `No country was found for "${filter}"`}
                  onClearSelection={() => updateField('country', {} as Country)}
                  onSelect={(_ev, v) => updateField('country', countries.find(c => c.alpha2 === v) || {} as Country)}
                />
              </FormGroup>

              <FormGroup label="Email" isRequired fieldId="email">
                <TextInput isRequired type="email" id="email" name="email"
                  value={tss.email || ''}
                  onChange={(_ev, v) => updateValidatedField('email', v)}
                  validated={validated.email}
                />
              </FormGroup>

              <FormGroup label="Mobile" fieldId="mobile">
                <PhoneInput id="mobile" name="mobile" value={tss.mobileNumber || '+44'}
                  onChange={handleMobileChange}
                />
              </FormGroup>
            </Form>
          </Card>
        </Stack>
      </PageSection>
    </EntityChangeLog>
  );
};

export { TSSDetail };
